import React, { useEffect, useReducer } from "react";
import {
  Row,
  Col,
  Form,
  FormControl,
  FormGroup,
  Button,
  Container,
} from "react-bootstrap";
import { Currency } from "../lib/components/Currency";
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from "../lib/components/LoadingSpinner";
import { useParams, useNavigate } from "react-router-dom";
import { withHeaderMessages } from "../higherOrderComponents/WithHeaderMessages";
import { ContractLayout } from "./ContractLayout";
import moment from "moment";

const ContractDisbursementQuoteRequest = (props) => {
  const initialState = {
    isLoading: true,
    isError: false,
    terminationDate: "",
    reason: "",
    valueOfService: "",
    disbursement: {},
    purchases: [],
    disclosureAgreementSelected: false,
    contract: {
      provider: {
        address: {},
      },
      purchaser: {
        address: {},
      },
      recipients: [],
    },
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case "contractDetails":
        return {
          ...state,
          contract: action.payload,
        };
      case "disbursementDetails":
        return {
          ...state,
          disbursement: action.payload,
          reason:
            action.payload.reasons.length &&
            action.payload.reasons.length > 1 &&
            action.payload.reasons[1],
          purchases: action.payload.purchases.map((p) => {
            return { selected: false, purchase: p };
          }),
          isLoading: false,
        };
      case "reason":
        return {
          ...state,
          reason: action.payload,
        };
      case "terminationDate":
        return {
          ...state,
          terminationDate: action.payload,
        };
      case "valueOfService":
        return {
          ...state,
          valueOfService: action.payload,
        };
      case "allPurchases":
        return {
          ...state,
          purchases: action.payload.purchases,
        };
      case "selectedPurchases":
        return {
          ...state,
          purchases: action.payload,
        };
      case "disclosureAgreement":
        return {
          ...state,
          disclosureAgreementSelected: action.payload,
        };
      case "hideLoader":
        return {
          ...state,
          isLoading: false,
        };
      case "error":
        return {
          ...state,
          isError: action.payload,
          isLoading: false,
        };
      default:
        throw new Error();
    }
  };

  const [contractDisbursementState, dispatch] = useReducer(
    reducer,
    initialState
  );
  const { number } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    async function getContractDetails() {
      try {
        props.header.setHeader("contractQuote");
        const res = await props.api.getContract(`/api/contract/${number}`);
        dispatch({ type: "contractDetails", payload: res.data });
      } catch {
        dispatch({ type: "error", payload: true });
      }
    }
    getContractDetails();
  }, []);

  useEffect(() => {
    async function getDisbursementDetails() {
      try {
        const res = await props.api.getContractDisbursementDefaults(
          `/api/contract/${number}/disbursement-defaults`
        );
        dispatch({ type: "disbursementDetails", payload: res.data });
      } catch {
        dispatch({ type: "error", payload: true });
      }
    }
    getDisbursementDetails();
  }, []);

  const hideLoader = () => {
    dispatch({ type: "hideLoader" });
  };

  const handleReasonChanged = (event) => {
    dispatch({ type: "reason", payload: event.target.value });
  };

  const handleTerminationDateChanged = (event) => {
    dispatch({ type: "terminationDate", payload: event.target.value });
  };

  const isReasonValid = () => {
    return contractDisbursementState.reason
      ? "was-validated"
      : "needs-validation";
  };

  const isTerminationDateValid = () => {
    if (!contractDisbursementState.terminationDate) {
      return null;
    }

    return moment(contractDisbursementState.terminationDate).isValid()
      ? "was-validated"
      : "needs-validation";
  };

  const isValueOfServiceValueValid = () => {
    const { valueOfService, disbursement } = contractDisbursementState;

    if (disbursement.excessToProvider === true) {
      return "was-validated";
    }

    if (!valueOfService) {
      return "needs-validation";
    }

    return (valueOfService + "").match(/^\d+(\.\d{2})?$/)
      ? "was-validated"
      : "needs-validation";
  };

  const handleValueOfServiceChanged = (event) => {
    dispatch({ type: "valueOfService", payload: event.target.value });
  };

  const handleSelectAllPurchases = (event) => {
    dispatch({
      type: "allPurchases",
      payload: {
        purchases: contractDisbursementState.purchases.map((p) => {
          return { selected: event.target.checked, purchase: p.purchase };
        }),
      },
    });
  };

  const handleSelectPurchase = (purchase, event) => {
    let newPurchases = contractDisbursementState.purchases;
    newPurchases.forEach((p) => {
      if (p === purchase) {
        p.selected = event.target.checked;
      }
    });
    dispatch({ type: "selectedPurchases", payload: newPurchases });
  };

  const allPurchasesSelected = () => {
    return (
      contractDisbursementState.purchases.filter((p) => !p.selected).length ===
      0
    );
  };

  const handleDisclosureAgreementSelectedChanged = (event) => {
    dispatch({ type: "disclosureAgreement", payload: event.target.checked });
  };

  const canQuoteBeRequested = () => {
    return !(
      contractDisbursementState.purchases.filter((p) => p.selected).length >
        0 &&
      contractDisbursementState.disclosureAgreementSelected &&
      contractDisbursementState.reason &&
      isReasonValid() === "was-validated" &&
      contractDisbursementState.terminationDate &&
      isTerminationDateValid() === "was-validated" &&
      isValueOfServiceValueValid() === "was-validated"
    );
  };

  const handleRequestQuoteFormSubmit = (event) => {
    event.preventDefault();
    navigate(
      `/contracts/${contractDisbursementState.disbursement.contractNumber}/disbursement-quote`,
      {
        state: {
          disbursementSelections: {
            reason: contractDisbursementState.reason,
            terminationDate: contractDisbursementState.terminationDate,
            valueOfService: contractDisbursementState.valueOfService,
            purchases: contractDisbursementState.purchases
              .filter((p) => p.selected)
              .map((p) => p.purchase),
          },
        },
      }
    );
  };

  const { contract, isError } = contractDisbursementState;
  return (
    <Container>
      {contractDisbursementState.isLoading && !isError ? (
        <LoadingSpinner />
      ) : isError ? (
          <ContractLayout
            contractNumber={number}
            contractStatus={contract.status}
          ></ContractLayout>
      ) : (
        <div>
          <ContractLayout
            contractNumber={number}
            contractStatus={contract.status}
          >
            <Form onSubmit={handleRequestQuoteFormSubmit} noValidate>
              <Row className="mt-3">
                <Col sm={12} lg={4} xl={3}>
                  <FormGroup controlId="reason" className={isReasonValid()}>
                    <Row className="justify-content-start">
                      <Col lg={3} xl={3} className="mt-1">
                        <Form.Label>Reason</Form.Label>
                      </Col>
                      <Col lg={7} xl={7} className="text-start p-0">
                        <Form.Select
                          componentclass="select"
                          className="form-control was-validated"
                          name="reason"
                          onChange={handleReasonChanged}
                          value={contractDisbursementState.reason}
                        >
                          {contractDisbursementState.disbursement.reasons &&
                            contractDisbursementState.disbursement.reasons.map(
                              (r, i) => (
                                <option value={r} key={i}>
                                  {r}
                                </option>
                              )
                            )}
                        </Form.Select>
                      </Col>
                    </Row>
                  </FormGroup>
                </Col>
                <Col sm={12} lg={4} xl={5}>
                  <FormGroup
                    controlId="terminationDate"
                    className={isTerminationDateValid()}
                  >
                    <Row className="justify-content-start">
                      <Col
                        md={12}
                        lg={5}
                        xl={3}
                        className="text-start mt-1 p-0"
                      >
                        <Form.Label>Date of Death</Form.Label>
                      </Col>
                      <Col md={12} lg={6} xl={7} className="text-start p-0 m-0">
                        <FormControl
                          type="date"
                          name="terminationDate"
                          placeholder="terminationDate"
                          value={contractDisbursementState.terminationDate}
                          onChange={handleTerminationDateChanged}
                        />
                        <FormControl.Feedback />
                      </Col>
                    </Row>
                  </FormGroup>
                </Col>
                <Col sm={12} lg={4}>
                  <FormGroup
                    controlId="valueOfService"
                    className={isValueOfServiceValueValid()}
                  >
                    <Row>
                      <Col md={12} lg={6} xl={6} className="me-auto mt-1 p-0">
                        <Form.Label>At-Need Service Value</Form.Label>
                      </Col>
                      <Col
                        md={12}
                        lg={6}
                        xl={6}
                        className="text-start p-0 me-auto"
                      >
                        <FormControl
                          type="text"
                          name="valueOfService"
                          placeholder="At Need Service Value"
                          value={contractDisbursementState.valueOfService}
                          onChange={handleValueOfServiceChanged}
                        />
                        <FormControl.Feedback />
                      </Col>
                    </Row>
                  </FormGroup>
                </Col>
              </Row>
              <Col sm={12}>
                <table className="table provider-location-table mt-3">
                  <thead>
                    <tr>
                      <th className="col-sm-2 p-1">
                        <Form.Group>
                          <Row>
                            <Col className="col-7">
                              <Form.Label> Include All Items</Form.Label>
                            </Col>
                            <Col>
                              <Form.Check
                                onChange={handleSelectAllPurchases}
                                checked={allPurchasesSelected()}
                              ></Form.Check>
                            </Col>
                          </Row>
                        </Form.Group>
                      </th>
                      <th>Purchase</th>
                      <th>Recipient(s)</th>
                      <th style={{ textAlign: "right" }}>Plan Amount</th>
                      <th style={{ textAlign: "right" }}>Trusted</th>
                      <th style={{ textAlign: "right" }}>Retained</th>
                      <th style={{ textAlign: "right" }}>Income</th>
                      <th>Guaranteed?</th>
                      <th style={{ textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractDisbursementState.purchases &&
                      contractDisbursementState.purchases.map((p, i) => (
                        <tr key={i}>
                          <td className="text-center">
                            <Form.Check
                              checked={p.selected}
                              onChange={(event) =>
                                handleSelectPurchase(p, event)
                              }
                            />
                          </td>
                          <td>
                            {p.purchase.group} - {p.purchase.category} -{" "}
                            {p.purchase.description}
                          </td>
                          <td>
                            {p.purchase.recipients.map((recipient, index) => {
                              return <p key={index}>{recipient}</p>;
                            })}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <Currency amount={p.purchase.netPrice} />
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <Currency amount={p.purchase.totalTrusted} />
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <Currency amount={p.purchase.totalRetained} />
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <Currency
                              amount={
                                p.purchase.incomeAmount +
                                (contractDisbursementState.disbursement
                                  .payAtMarketValue
                                  ? p.purchase.totalUnrealizedGainOrLoss
                                  : 0)
                              }
                            />
                          </td>
                          <td>{p.purchase.guaranteed ? "Yes" : "No"}</td>
                          <td style={{ textAlign: "right" }}>
                            <Currency
                              amount={
                                p.selected
                                  ? p.purchase.principalAmount +
                                    p.purchase.incomeAmount +
                                    (contractDisbursementState.disbursement
                                      .payAtMarketValue
                                      ? p.purchase.totalUnrealizedGainOrLoss
                                      : 0)
                                  : 0
                              }
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </Col>
              <Row>
                <Col>
                  <p style={{ marginBottom: "25px", color: "#6C6C6D" }}>
                    Distribution Quote Disclosure: Quoted values are based on
                    all activity processed through the previous day; the amount
                    is subject to change based on additional trust activity
                    including income, investment gains, and fees.
                  </p>
                </Col>
              </Row>
              <Row className="mx-0">
                <Col className="col-auto p-1">
                  <Form.Check
                    checked={
                      contractDisbursementState.disclosureAgreementSelected
                    }
                    onChange={handleDisclosureAgreementSelectedChanged}
                  />
                </Col>
                <Col className="col-11 p-1">
                  I acknowledge I have read the distribution quote disclosure
                  above and understand that actual payment received may differ
                  from the value quoted above.
                </Col>
              </Row>
              <Row>
                <Col className="text-start">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={canQuoteBeRequested()}
                  >
                    REQUEST QUOTE
                  </Button>
                </Col>
              </Row>
            </Form>
          </ContractLayout>
        </div>
      )}
    </Container>
  );
};

export default withHeaderMessages(
  WithApiAccess(ContractDisbursementQuoteRequest)
);
